import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { RichTextField } from 'components/RichTextEditor/RichTextField'
import { BaseFile } from 'config'
import { hardcodedEmailRecipientOptions } from 'pages/Admin/AdminTools/Configuration/EmailTexts/hardcodedEmailRecipients'
import { useEffect, useMemo, useState } from 'react'
import { getAdminToAeEmails } from 'services'
import { getOriginalTemplate } from 'services/apis/conditions'
import { Button, InputFileTable, Modal, MultiSelect } from 'stories/components'
import { getEmailHtml } from 'utils/emailHtml'
import { validateEmail } from 'utils/validator'

export interface IEmailElement {
  type: string
  data: any[]
  showIf?: string
}

export interface ICustomerEmail {
  title: string
  description: string
  note: string
  subject: string
}

interface EmailEditorModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: {
    subject: string
    elements?: IEmailElement[]
    customerEmail?: ICustomerEmail | null
    files: BaseFile[]
    emailTo?: string[]
    emailCc?: string[]
    emailBcc?: string[]
    emailExclude?: string[]
  }) => Promise<void>
  emailKey: string
  subject: string
  elements: IEmailElement[]
  customerEmail?: ICustomerEmail | null
  originalPlaceholders: string[]
  placeholders: string[]
  files?: BaseFile[]
  /** To + Exclude (allowlisted automatic-To emails only). */
  supportsRecipients?: boolean
  /** Cc + Bcc — every hardcoded email. */
  supportsCcBcc?: boolean
  emailTo?: string[]
  emailCc?: string[]
  emailBcc?: string[]
  emailExclude?: string[]
}

const ELEMENT_TYPES = [
  { value: 'element1', label: 'Title (centered blue)' },
  { value: 'element2', label: 'Paragraph (centered)' },
  { value: 'element3', label: 'Property: label + bold value' },
  { value: 'element4', label: 'Property: label + colored value' },
  { value: 'element5', label: 'Sub-title (centered)' },
  { value: 'element6', label: 'Button link' },
  { value: 'element7', label: 'Property: horizontal (label: value)' },
  { value: 'element8', label: 'Sub-title (left)' },
  { value: 'element9', label: 'Two buttons (Yes / No)' },
  { value: 'element10', label: 'Property with HTML value' },
  { value: 'element11', label: 'Paragraph (left)' },
  { value: 'link', label: 'Inline link' },
]

const ELEMENT_FIELDS: Record<string, string[]> = {
  element1: ['Text'],
  element2: ['Text'],
  element3: ['Label', 'Value'],
  element4: ['Label', 'Value'],
  element5: ['Text'],
  element6: ['URL', 'Button Label'],
  element7: ['Label', 'Value'],
  element8: ['Text'],
  element9: ['URL (Yes)', 'Label (Yes)', 'URL (No)', 'Label (No)'],
  element10: ['Label', 'HTML Value'],
  element11: ['Text'],
  link: ['URL', 'Label'],
  table: [],
}

/**
 * Which field of each element type is body text, and so worth a formatting
 * toolbar. The renderers in `utils/emailHtml.ts` interpolate these inside
 * `<h1>` / `<h2>` / `<h4>` / `<span>`, so the editor runs in 'inline' mode.
 *
 * Deliberately excluded:
 *  - every URL and every button / link label (element6, element9, link), where
 *    markup would land in an `href` or break the styled anchor;
 *  - element10's "HTML Value", which is raw hand-written HTML (tables and the
 *    like) that the editor's schema would strip on save.
 */
const ELEMENT_RICH_FIELDS: Record<string, number[]> = {
  element1: [0],
  element2: [0],
  element3: [1],
  element4: [1],
  element5: [0],
  element7: [1],
  element8: [0],
  element11: [0],
}

function normalizeData(type: string, existing: any[]): any[] {
  const fields = ELEMENT_FIELDS[type] ?? []
  if (type === 'table') return existing
  return fields.map((_, i) => existing?.[i] ?? '')
}

function renderCustomerEmailPreview(ce: ICustomerEmail): string {
  return `
<html>
<body style="margin:0; padding:20px; background:#e5e5e5; font-family: Arial, sans-serif;">
  <div style="background:#fff; max-width:700px; margin:0 auto; border-radius:10px; border-top:10px solid #449ccc; border-bottom:10px solid #449ccc; padding:30px 20px;">
    <h1 style="text-align:center; font-size:19.5pt; margin:0 0 20px; font-weight:bold;">${ce.title || ''}</h1>
    <p style="text-align:center; font-size:16pt; color:#003d6b; margin:0 0 10px;">Dear {{full_name}}!</p>
    <p style="text-align:center; font-size:12pt; margin:0 0 30px;">${ce.description || ''}<span style="color:black">${
    ce.note || ''
  }</span></p>
    <p style="text-align:center; margin:20px 0;">
      <a href="#" style="color:#fff;background-color:#003d6b;padding:10px 24px;text-decoration:none;font-size:12.5pt;">${
        ce.subject || ''
      }</a>
    </p>
  </div>
</body>
</html>
`
}

export function EmailEditorModal({
  isOpen,
  onClose,
  onSubmit,
  emailKey,
  subject: initialSubject,
  elements: initialElements,
  customerEmail: initialCustomerEmail,
  originalPlaceholders,
  placeholders,
  files: initialFiles = [],
  supportsRecipients = false,
  supportsCcBcc = false,
  emailTo: initialEmailTo = [],
  emailCc: initialEmailCc = [],
  emailBcc: initialEmailBcc = [],
  emailExclude: initialEmailExclude = [],
}: EmailEditorModalProps) {
  const isCustomerEmail = !!initialCustomerEmail
  const [subject, setSubject] = useState(initialSubject)
  const [elements, setElements] = useState<IEmailElement[]>([])
  const [customerEmail, setCustomerEmail] = useState<ICustomerEmail>({
    title: '',
    description: '',
    note: '',
    subject: '',
  })
  const [files, setFiles] = useState<BaseFile[]>([])
  const [emailTo, setEmailTo] = useState<string[]>([])
  const [emailCc, setEmailCc] = useState<string[]>([])
  const [emailBcc, setEmailBcc] = useState<string[]>([])
  const [emailExclude, setEmailExclude] = useState<string[]>([])
  const [userEmails, setUserEmails] = useState<string[]>([])
  const [originalTemplate, setOriginalTemplate] = useState('')
  const [isSubmitting, setSubmitting] = useState(false)
  const recipientOptions = hardcodedEmailRecipientOptions()
  const excludeOptions = useMemo(() => {
    const options: Record<string, string> = {}
    userEmails.forEach((email) => (options[email] = email))
    return options
  }, [userEmails])

  useEffect(() => {
    if (isOpen) {
      setSubject(initialSubject)
      setElements(cloneDeep(initialElements))
      setFiles(cloneDeep(initialFiles || []))
      if (initialCustomerEmail) setCustomerEmail(cloneDeep(initialCustomerEmail))
      setEmailTo(cloneDeep(initialEmailTo || []))
      setEmailCc(cloneDeep(initialEmailCc || []))
      setEmailBcc(cloneDeep(initialEmailBcc || []))
      setEmailExclude(cloneDeep(initialEmailExclude || []))
    }
  }, [
    isOpen,
    initialSubject,
    initialElements,
    initialCustomerEmail,
    initialFiles,
    initialEmailTo,
    initialEmailCc,
    initialEmailBcc,
    initialEmailExclude,
  ])

  useEffect(() => {
    if (!isOpen || !supportsRecipients) return
    getAdminToAeEmails().then((emails: string[]) => setUserEmails(Array.isArray(emails) ? emails : []))
  }, [isOpen, supportsRecipients])

  const showRecipientBlock = supportsRecipients || supportsCcBcc

  useEffect(() => {
    if (!isCustomerEmail) {
      getOriginalTemplate().then((res: string) => setOriginalTemplate(res))
    }
  }, [isCustomerEmail])

  const allPlaceholders = Array.from(new Set([...originalPlaceholders, ...placeholders]))

  const previewHtml = isCustomerEmail
    ? renderCustomerEmailPreview(customerEmail)
    : originalTemplate
    ? getEmailHtml(
        elements.filter((e) => e && e.type !== 'table'),
        originalTemplate,
      )
    : ''

  const updateElement = (index: number, field: 'type' | number, value: string) => {
    const updated = cloneDeep(elements)
    if (field === 'type') {
      updated[index] = { type: value, data: normalizeData(value, updated[index].data) }
    } else {
      updated[index].data[field] = value
    }
    setElements(updated)
  }

  const moveElement = (index: number, dir: -1 | 1) => {
    const updated = cloneDeep(elements)
    const target = index + dir
    if (target < 0 || target >= updated.length) return
    ;[updated[index], updated[target]] = [updated[target], updated[index]]
    setElements(updated)
  }

  const deleteElement = (index: number) => {
    const updated = cloneDeep(elements)
    updated.splice(index, 1)
    setElements(updated)
  }

  const addElement = () => {
    setElements([...elements, { type: 'element2', data: [''] }])
  }

  const updateCustomerField = (field: keyof ICustomerEmail, value: string) => {
    setCustomerEmail({ ...customerEmail, [field]: value })
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const recipientPayload = {
        ...(supportsCcBcc || supportsRecipients ? { emailCc, emailBcc } : {}),
        ...(supportsRecipients ? { emailTo, emailExclude } : {}),
      }
      if (isCustomerEmail) {
        await onSubmit({ subject, customerEmail, files, ...recipientPayload })
      } else {
        await onSubmit({ subject, elements, files, ...recipientPayload })
      }
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(`{${text}}`).catch(() => {})
  }

  return (
    <Modal title="Edit Email" isOpen={isOpen} onClose={onClose} titleOkay="" titleCancel="">
      <div className="w-[1400px] flex flex-col gap-4">
        {/* Placeholders */}
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            Placeholders — click to copy
            <span className="ml-2 font-normal normal-case text-gray-400">(bold = required by original template)</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {allPlaceholders.map((p) => {
              const isOriginal = originalPlaceholders.includes(p)
              return (
                <span
                  key={p}
                  title={`Copy {${p}}`}
                  onClick={() => copyToClipboard(p)}
                  className={`px-2 py-0.5 rounded text-xs cursor-pointer select-none border transition-colors hover:bg-blue-50 hover:border-blue-300 ${
                    isOriginal
                      ? 'font-bold border-blue-300 bg-blue-50 text-blue-700'
                      : 'font-normal border-gray-200 bg-white text-gray-600'
                  }`}
                >
                  {`{${p}}`}
                </span>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Editor column */}
          <div className="flex flex-col gap-3 overflow-y-auto max-h-[600px] pr-1">
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
              <input
                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {showRecipientBlock && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Recipients
                  <span className="ml-2 font-normal normal-case text-gray-400">
                    Loan parties, company mailboxes, and context specials
                  </span>
                </div>
                {supportsRecipients && (
                  <MultiSelect
                    id={`${emailKey}-emailTo`}
                    title="To"
                    options={recipientOptions}
                    value={emailTo}
                    onChange={(value) => setEmailTo(Array.isArray(value) ? value : [])}
                    defaultSelected={false}
                    sort
                    sortKey="value"
                    allowCustomValues
                    customValuePlaceholder="Add email address"
                    customValueValidator={validateEmail}
                  />
                )}
                {supportsCcBcc && (
                  <>
                    <MultiSelect
                      id={`${emailKey}-emailCc`}
                      title="Cc"
                      options={recipientOptions}
                      value={emailCc}
                      onChange={(value) => setEmailCc(Array.isArray(value) ? value : [])}
                      defaultSelected={false}
                      sort
                      sortKey="value"
                      allowCustomValues
                      customValuePlaceholder="Add email address"
                      customValueValidator={validateEmail}
                    />
                    <MultiSelect
                      id={`${emailKey}-emailBcc`}
                      title="Bcc"
                      options={recipientOptions}
                      value={emailBcc}
                      onChange={(value) => setEmailBcc(Array.isArray(value) ? value : [])}
                      defaultSelected={false}
                      sort
                      sortKey="value"
                      allowCustomValues
                      customValuePlaceholder="Add email address"
                      customValueValidator={validateEmail}
                    />
                  </>
                )}
                {supportsRecipients && (
                  <MultiSelect
                    id={`${emailKey}-emailExclude`}
                    title="Exclude"
                    options={excludeOptions}
                    value={emailExclude}
                    onChange={(value) => setEmailExclude(Array.isArray(value) ? value : [])}
                    defaultSelected={false}
                    sort
                    sortKey="value"
                    allowCustomValues
                    customValuePlaceholder="Add email address"
                    customValueValidator={validateEmail}
                    tooltip="Removed after roles expand. Use this when someone matches a recipient but should not get this email."
                  />
                )}
              </div>
            )}

            {isCustomerEmail ? (
              <>
                {/* title / description / note land inside <span> tags in
                    `sendCustomerEmail`, so these run in 'inline' mode. */}
                <RichTextField
                  title="Title"
                  variant="inline"
                  minHeight={90}
                  placeholders={allPlaceholders}
                  value={customerEmail.title}
                  onChange={(value) => updateCustomerField('title', value)}
                />
                <RichTextField
                  title="Description"
                  variant="inline"
                  minHeight={140}
                  placeholders={allPlaceholders}
                  value={customerEmail.description}
                  onChange={(value) => updateCustomerField('description', value)}
                />
                <RichTextField
                  title="Note"
                  variant="inline"
                  minHeight={90}
                  placeholders={allPlaceholders}
                  value={customerEmail.note}
                  onChange={(value) => updateCustomerField('note', value)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Label</label>
                  <input
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={customerEmail.subject}
                    onChange={(e) => updateCustomerField('subject', e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-gray-700">Elements</div>
                {elements
                  .filter((v) => !!v)
                  .map((el, idx) => {
                    const fields = ELEMENT_FIELDS[el.type] ?? []
                    return (
                      <div key={idx} className="border rounded-lg p-3 bg-white shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <select
                            className="flex-1 border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={el.type}
                            onChange={(e) => updateElement(idx, 'type', e.target.value)}
                          >
                            {ELEMENT_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.value} — {t.label}
                              </option>
                            ))}
                            {el.type === 'table' && <option value="table">table — Table</option>}
                          </select>
                          <button
                            onClick={() => moveElement(idx, -1)}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Move up"
                          >
                            <ArrowUpIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => moveElement(idx, 1)}
                            className="p-1 rounded hover:bg-gray-100"
                            title="Move down"
                          >
                            <ArrowDownIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => deleteElement(idx)}
                            className="p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4 text-red-400" />
                          </button>
                        </div>

                        {el.showIf && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">
                              Conditional — shown when <span className="font-semibold">{el.showIf}</span> is present
                            </span>
                          </div>
                        )}

                        {el.type === 'table' ? (
                          <p className="text-xs text-gray-400 italic">Table elements cannot be edited here.</p>
                        ) : (
                          fields.map((fieldLabel, fi) =>
                            (ELEMENT_RICH_FIELDS[el.type] || []).includes(fi) ? (
                              <div key={fi} className="mb-2">
                                <RichTextField
                                  title={fieldLabel}
                                  variant="inline"
                                  minHeight={90}
                                  placeholders={allPlaceholders}
                                  value={el.data?.[fi] ?? ''}
                                  onChange={(value) => updateElement(idx, fi, value)}
                                />
                              </div>
                            ) : (
                              <div key={fi} className="mb-2">
                                <label className="block text-xs text-gray-500 mb-0.5">{fieldLabel}</label>
                                <textarea
                                  rows={2}
                                  className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y"
                                  value={el.data?.[fi] ?? ''}
                                  onChange={(e) => updateElement(idx, fi, e.target.value)}
                                />
                              </div>
                            ),
                          )
                        )}
                      </div>
                    )
                  })}

                <button
                  onClick={addElement}
                  className="flex items-center gap-1 text-sm text-shade-blue hover:underline py-1"
                >
                  <PlusIcon className="w-4 h-4" /> Add Element
                </button>
              </>
            )}

            <div className="border-t pt-3 mt-1">
              <InputFileTable
                title="Attachment Files"
                filePath={`emailHardcoded/${emailKey}`}
                value={files as any}
                onChange={(value) => setFiles(value)}
                multiple
                showStatus={false}
                showCategory={false}
              />
              <p className="text-xs text-gray-400 mt-1">
                These files are attached automatically whenever this hardcoded email is sent.
              </p>
            </div>
          </div>

          {/* Preview column */}
          <div className="flex flex-col gap-2">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Subject Preview</div>
              <p className="text-sm text-gray-800 border rounded px-3 py-2 bg-gray-50 min-h-[32px]">{subject}</p>
            </div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email Preview</div>
            <div className="border rounded-lg overflow-hidden flex-1">
              {previewHtml ? (
                <iframe
                  title="Email Preview"
                  srcDoc={previewHtml}
                  className="w-full"
                  style={{ height: '520px', border: 'none' }}
                />
              ) : (
                <div className="h-[520px] flex items-center justify-center text-gray-400 text-sm">Loading preview…</div>
              )}
            </div>
            {!!files.length && (
              <div className="text-xs text-gray-500">
                Attachments: {files.map((f) => f.name).join(', ')}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <Button color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting} className="px-8">
            Save
          </Button>
        </div>
      </div>
    </Modal>
  )
}

